/**
 * Track segment geometry builders.
 *
 * Shared by Point Draw and drag-and-drop track placement. Given entry points,
 * headings, and sizing, these helpers return everything needed to commit an
 * `overhead-rail` LayoutElement whose endpoints match `topology.getRailEndpoints`
 * so rails line up end-to-end and validate as a connected run.
 */

export type TrackSegmentType = 'straight' | 'curve45' | 'curve90' | 'curve180';

const curveSweepDeg = (type: TrackSegmentType): number => {
  if (type === 'curve180') return 180;
  if (type === 'curve90') return 90;
  if (type === 'curve45') return 45;
  return 0;
};

export interface ChainSizing {
  /** Length of a straight segment, in world units. */
  lengthUnits: number;
  /** Track thickness (straight segment height), in world units. */
  thicknessUnits: number;
  /** Curve radius, in world units. */
  radiusUnits: number;
}

export interface ChainSegmentGeometry {
  /** Top-left for straights, arc centre for curves. */
  position: { x: number; y: number };
  rotation: number;
  dimensions: { width: number; height: number };
  railType: TrackSegmentType;
  /** Far endpoint of the segment. */
  exit: { x: number; y: number };
  /** Flat [x0,y0,x1,y1,...] world-space polyline for drawing a live preview. */
  points: number[];
  /**
   * Sweep direction for curves (+1 / −1). Matches `customProperties.curveSweepSign`
   * / `topology.getCurveSweepSign`. Omitted for straights.
   */
  curveSweepSign?: 1 | -1;
  /**
   * Intended travel heading (deg) along the caller’s polyline, before axis-aligned
   * straights are canonicalized to 0°/90°. Point Draw uses this to reverse uni
   * rails whose stored rotation opposes draw order (west/north legs).
   */
  travelHeadingDeg?: number;
}

export type OrthogonalPathRejectReason =
  | 'too-few-points'
  | 'diagonal-segment'
  | 'invalid-corner'
  | 'insufficient-straight'
  | 'overlapping-path';

export type OrthogonalPathBuildResult =
  | { ok: true; pieces: ChainSegmentGeometry[] }
  | { ok: false; reason: OrthogonalPathRejectReason; segmentIndex?: number; vertexIndex?: number };

const toRad = (deg: number): number => (deg * Math.PI) / 180;
const normDeg = (deg: number): number => ((deg % 360) + 360) % 360;

/**
 * Builds the geometry for a track segment leaving `entry` along `directionDeg`.
 *
 * Straight: the segment leaves `entry` heading along `directionDeg` for
 * `sizing.lengthUnits`. Its first short-edge midpoint lands on `entry`.
 *
 * Curve: `directionDeg` is the entry tangent (the way the track is heading as it
 * enters the bend). The arc sweeps by the segment angle, so the exit heading is
 * `directionDeg + sweep`. The arc's start endpoint lands on `entry`, matching
 * `topology.getRailEndpoints` (centre at element position, start at local 0°).
 */
export function computeChainSegment(
  entry: { x: number; y: number },
  directionDeg: number,
  type: TrackSegmentType,
  sizing: ChainSizing
): ChainSegmentGeometry {
  const dirRad = toRad(directionDeg);
  const dir = { x: Math.cos(dirRad), y: Math.sin(dirRad) };

  if (type === 'straight') {
    const w = Math.max(1, sizing.lengthUnits);
    const h = Math.max(0.001, sizing.thicknessUnits);
    const cos = Math.cos(dirRad);
    const sin = Math.sin(dirRad);
    // Local first-endpoint is (0, h/2); rotate it by the heading and offset the
    // top-left position so that endpoint sits exactly on `entry`.
    const offX = -(h / 2) * sin;
    const offY = (h / 2) * cos;
    const position = { x: entry.x - offX, y: entry.y - offY };
    const exit = { x: entry.x + w * dir.x, y: entry.y + w * dir.y };
    return {
      position,
      rotation: normDeg(directionDeg),
      dimensions: { width: w, height: h },
      railType: type,
      exit,
      points: [entry.x, entry.y, exit.x, exit.y],
    };
  }

  const theta = curveSweepDeg(type);
  const R = Math.max(0.001, sizing.radiusUnits);
  // Element rotation so the arc's start tangent (+y local) points along the
  // chosen heading.
  const r = directionDeg - 90;
  const rRad = toRad(r);
  const center = {
    x: entry.x - R * Math.cos(rRad),
    y: entry.y - R * Math.sin(rRad),
  };
  const exitRad = toRad(theta + r);
  const exit = {
    x: center.x + R * Math.cos(exitRad),
    y: center.y + R * Math.sin(exitRad),
  };

  const segments = Math.max(8, Math.ceil(theta / 5));
  const points: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = toRad(r + (theta * i) / segments);
    points.push(center.x + R * Math.cos(a), center.y + R * Math.sin(a));
  }

  return {
    position: center,
    rotation: normDeg(r),
    dimensions: { width: R, height: R },
    railType: type,
    exit,
    points,
    // Do NOT set curveSweepSign here. Curve90 Point Draw fillets encode
    // direction in position/rotation (see computeFilletCurve). Stamping a
    // sweep sign onto Curve90 double-applies CCW and breaks ordinary 90° turns.
    // Only computeUTurn180 sets curveSweepSign for the Curve180 primitive.
  };
}

/**
 * Straight-segment geometry between two arbitrary world points.
 *
 * Axis-aligned straights are stored in a canonical orientation so identical
 * footprints do not depend on click order:
 *   - horizontal → left → right (rotation 0°)
 *   - vertical   → top  → bottom (rotation 90°)
 *
 * `travelHeadingDeg` preserves the caller’s a→b travel direction so Point Draw
 * can reverse uni rails after commit when canonicalization flipped the arrow.
 *
 * Reuses the same top-left/offset convention as `computeChainSegment` (the
 * segment's first short-edge midpoint lands on `a`, the far midpoint on `b`),
 * so point-drawn rails share endpoints exactly with `topology.getRailEndpoints`
 * — keeping snapping, validation, and simulation consistent with rails created
 * by the drag-and-drop workflow.
 */
export function computeSegmentBetween(
  a: { x: number; y: number },
  b: { x: number; y: number },
  sizing: ChainSizing
): ChainSegmentGeometry {
  let start = a;
  let end = b;
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const epsilon = 1e-6;
  const horizontal = Math.abs(dy) <= epsilon;
  const vertical = Math.abs(dx) <= epsilon;
  const travelHeadingDeg = normDeg((Math.atan2(dy, dx) * 180) / Math.PI);
  // Swap before geometry so horizontal rails are always 0° and vertical always 90°.
  if (horizontal && dx < 0) {
    start = b;
    end = a;
  } else if (vertical && dy < 0) {
    start = b;
    end = a;
  }
  const ndx = end.x - start.x;
  const ndy = end.y - start.y;
  const length = Math.hypot(ndx, ndy);
  const directionDeg = (Math.atan2(ndy, ndx) * 180) / Math.PI;
  return {
    ...computeChainSegment(start, directionDeg, 'straight', {
      ...sizing,
      lengthUnits: Math.max(1, length),
    }),
    travelHeadingDeg,
  };
}

/**
 * A fixed-radius 90° corner (fillet) that turns an incoming straight heading
 * (`headingDeg`) by ±90° while staying a *valid, unstretched* curve component.
 *
 * `entry` is the tangent point where the curve meets the (already-shortened)
 * incoming straight; the returned `exit` is the tangent point where the
 * outgoing straight resumes. `sign = +1` turns clockwise (screen space, +y
 * down); `sign = -1` turns counter-clockwise. The curve always uses
 * `sizing.radiusUnits` — the radius is never scaled to fit, so callers must only
 * insert a fillet when both adjacent straights are at least one radius long.
 *
 * Clockwise turns reuse `computeChainSegment`'s proven curve construction;
 * counter-clockwise turns mirror it. Diameter U-turns are emitted as Curve180
 * by `buildOrthogonalTrackPath`; other large turns are still composed by the
 * caller from multiple 45°/90° pieces.
 */
export function computeFilletCurve(
  entry: { x: number; y: number },
  headingDeg: number,
  sizing: ChainSizing,
  sign: 1 | -1
): ChainSegmentGeometry {
  if (sign === 1) {
    return computeChainSegment(entry, headingDeg, 'curve90', sizing);
  }

  const theta = 90;
  const R = Math.max(0.001, sizing.radiusUnits);
  const rPrime = headingDeg + 90;
  const rp = toRad(rPrime);
  const center = {
    x: entry.x - R * Math.cos(rp),
    y: entry.y - R * Math.sin(rp),
  };
  const exitRad = toRad(rPrime - theta);
  const exit = {
    x: center.x + R * Math.cos(exitRad),
    y: center.y + R * Math.sin(exitRad),
  };

  const segments = Math.max(8, Math.ceil(theta / 5));
  const points: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = toRad(rPrime - (theta * i) / segments);
    points.push(center.x + R * Math.cos(a), center.y + R * Math.sin(a));
  }

  return {
    position: center,
    rotation: normDeg(rPrime - theta),
    dimensions: { width: R, height: R },
    railType: 'curve90',
    exit,
    points,
    // Intentionally omit curveSweepSign — CCW geometry is already encoded in
    // center/rotation for the default (+1) renderer sweep.
  };
}

/**
 * Fixed-radius 180° U-turn (Curve180 primitive).
 *
 * `entry` is where the inbound straight ends; `headingDeg` is the arrival
 * tangent. `sign = +1` turns clockwise (screen +y down); `sign = -1` turns
 * counter-clockwise. Exit lies one diameter (`2R`) to the side and the exit
 * heading is `headingDeg + 180°`.
 */
export function computeUTurn180(
  entry: { x: number; y: number },
  headingDeg: number,
  sizing: ChainSizing,
  sign: 1 | -1
): ChainSegmentGeometry {
  if (sign === 1) {
    return {
      ...computeChainSegment(entry, headingDeg, 'curve180', sizing),
      curveSweepSign: 1,
    };
  }

  const theta = 180;
  const R = Math.max(0.001, sizing.radiusUnits);
  const rPrime = headingDeg + 90;
  const rp = toRad(rPrime);
  const center = {
    x: entry.x - R * Math.cos(rp),
    y: entry.y - R * Math.sin(rp),
  };
  const exitRad = toRad(rPrime - theta);
  const exit = {
    x: center.x + R * Math.cos(exitRad),
    y: center.y + R * Math.sin(exitRad),
  };

  const segments = Math.max(8, Math.ceil(theta / 5));
  const points: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = toRad(rPrime - (theta * i) / segments);
    points.push(center.x + R * Math.cos(a), center.y + R * Math.sin(a));
  }

  return {
    position: center,
    rotation: normDeg(rPrime - theta),
    dimensions: { width: R, height: R },
    railType: 'curve180',
    exit,
    points,
    curveSweepSign: -1,
  };
}

/**
 * Build a manufactured orthogonal track path.
 *
 * This is the central direction-change invariant: every horizontal/vertical
 * change is represented by one fixed-radius Curve90 — except a diameter-span
 * U-turn (leg length ≈ 2R between two same-sense 90° corners), which emits a
 * single Curve180 instead of Curve90–Straight–Curve90. The operation is
 * all-or-nothing; if any segment cannot retain `minStraightUnits` after its
 * adjacent curves consume one configured radius each, no geometry is returned.
 * Curves are never omitted or shrunk to make an invalid corner fit.
 */
export function buildOrthogonalTrackPath(
  vertices: { x: number; y: number }[],
  sizing: ChainSizing,
  options: { closed?: boolean; minStraightUnits?: number } = {}
): OrthogonalPathBuildResult {
  const closed = options.closed === true;
  const minStraight = Math.max(0, options.minStraightUnits ?? 0);
  const radius = sizing.radiusUnits;
  const count = vertices.length;
  const segmentCount = closed ? count : count - 1;
  const epsilon = 1e-6;

  if (count < (closed ? 3 : 2) || segmentCount < 1 || radius <= epsilon) {
    return { ok: false, reason: 'too-few-points' };
  }

  const headings: number[] = [];
  const lengths: number[] = [];
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    const a = vertices[segmentIndex];
    const b = vertices[(segmentIndex + 1) % count];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const horizontal = Math.abs(dy) <= epsilon && Math.abs(dx) > epsilon;
    const vertical = Math.abs(dx) <= epsilon && Math.abs(dy) > epsilon;
    if (!horizontal && !vertical) {
      return { ok: false, reason: 'diagonal-segment', segmentIndex };
    }
    headings.push(horizontal ? (dx > 0 ? 0 : 180) : dy > 0 ? 90 : 270);
    lengths.push(Math.hypot(dx, dy));
  }

  const rangesOverlap = (a0: number, a1: number, b0: number, b1: number) =>
    Math.max(Math.min(a0, a1), Math.min(b0, b1)) <=
    Math.min(Math.max(a0, a1), Math.max(b0, b1)) + epsilon;
  for (let aIndex = 0; aIndex < segmentCount; aIndex++) {
    const aNext = (aIndex + 1) % count;
    const a0 = vertices[aIndex];
    const a1 = vertices[aNext];
    for (let bIndex = aIndex + 1; bIndex < segmentCount; bIndex++) {
      const bNext = (bIndex + 1) % count;
      const adjacent =
        aNext === bIndex ||
        bNext === aIndex ||
        (closed && aIndex === 0 && bNext === 0);
      if (adjacent) continue;
      const b0 = vertices[bIndex];
      const b1 = vertices[bNext];
      const aHorizontal = Math.abs(a0.y - a1.y) <= epsilon;
      const bHorizontal = Math.abs(b0.y - b1.y) <= epsilon;
      const intersects =
        aHorizontal === bHorizontal
          ? aHorizontal
            ? Math.abs(a0.y - b0.y) <= epsilon &&
              rangesOverlap(a0.x, a1.x, b0.x, b1.x)
            : Math.abs(a0.x - b0.x) <= epsilon &&
              rangesOverlap(a0.y, a1.y, b0.y, b1.y)
          : aHorizontal
            ? rangesOverlap(a0.x, a1.x, b0.x, b1.x) &&
              rangesOverlap(b0.y, b1.y, a0.y, a1.y)
            : rangesOverlap(b0.x, b1.x, a0.x, a1.x) &&
              rangesOverlap(a0.y, a1.y, b0.y, b1.y);
      if (intersects) {
        return { ok: false, reason: 'overlapping-path', segmentIndex: bIndex };
      }
    }
  }

  const corners = new Array<boolean>(count).fill(false);
  const cornerTurns = new Array<number>(count).fill(0);
  const cornerStart = closed ? 0 : 1;
  const cornerEnd = closed ? count : count - 1;
  for (let vertexIndex = cornerStart; vertexIndex < cornerEnd; vertexIndex++) {
    const incoming = headings[(vertexIndex - 1 + segmentCount) % segmentCount];
    const outgoing = headings[vertexIndex % segmentCount];
    const turn = normDeg(outgoing - incoming);
    if (turn !== 90 && turn !== 270) {
      return { ok: false, reason: 'invalid-corner', vertexIndex };
    }
    corners[vertexIndex] = true;
    cornerTurns[vertexIndex] = turn;
  }

  // Diameter U-turn → Curve180 ONLY when the span is a true reverse (two
  // same-sense 90° corners) AND the leg is exactly one diameter (2R).
  // Ordinary single-corner 90° turns never match this (they have one corner).
  // Curve90 fillets must not receive curveSweepSign (see computeFilletCurve).
  const curve180Bridge = new Array<boolean>(segmentCount).fill(false);
  const curve180Entry = new Array<boolean>(count).fill(false);
  const curve180Exit = new Array<boolean>(count).fill(false);
  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    const startVertex = segmentIndex;
    const endVertex = (segmentIndex + 1) % count;
    if (!corners[startVertex] || !corners[endVertex]) continue;
    if (cornerTurns[startVertex] !== cornerTurns[endVertex]) continue;
    if (Math.abs(lengths[segmentIndex] - 2 * radius) > epsilon) continue;
    curve180Bridge[segmentIndex] = true;
    curve180Entry[startVertex] = true;
    curve180Exit[endVertex] = true;
  }

  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    const startVertex = segmentIndex;
    const endVertex = (segmentIndex + 1) % count;
    if (curve180Bridge[segmentIndex]) {
      if (lengths[segmentIndex] < 2 * radius - epsilon) {
        return { ok: false, reason: 'insufficient-straight', segmentIndex };
      }
      continue;
    }
    const startConsumes =
      corners[startVertex] && !curve180Entry[startVertex] && !curve180Exit[startVertex]
        ? radius
        : 0;
    const endConsumes =
      corners[endVertex] && !curve180Entry[endVertex] && !curve180Exit[endVertex]
        ? radius
        : 0;
    const required = startConsumes + endConsumes + minStraight;
    if (lengths[segmentIndex] < required - epsilon) {
      return { ok: false, reason: 'insufficient-straight', segmentIndex };
    }
  }

  const direction = (heading: number) => {
    const radians = toRad(heading);
    return { x: Math.cos(radians), y: Math.sin(radians) };
  };
  const pieces: ChainSegmentGeometry[] = [];

  for (let segmentIndex = 0; segmentIndex < segmentCount; segmentIndex++) {
    const heading = headings[segmentIndex];
    const vector = direction(heading);
    const startVertex = segmentIndex;
    const endVertex = (segmentIndex + 1) % count;
    const rawStart = vertices[startVertex];
    const rawEnd = vertices[endVertex];

    if (curve180Bridge[segmentIndex]) {
      const arrivalHeading = headings[(segmentIndex - 1 + segmentCount) % segmentCount];
      const sign: 1 | -1 = cornerTurns[startVertex] === 90 ? 1 : -1;
      pieces.push(
        computeUTurn180(rawStart, arrivalHeading, { ...sizing, radiusUnits: radius }, sign)
      );
      continue;
    }

    const shortenStart =
      corners[startVertex] && !curve180Entry[startVertex] && !curve180Exit[startVertex];
    const shortenEnd =
      corners[endVertex] && !curve180Entry[endVertex] && !curve180Exit[endVertex];
    const start = shortenStart
      ? { x: rawStart.x + vector.x * radius, y: rawStart.y + vector.y * radius }
      : rawStart;
    const end = shortenEnd
      ? { x: rawEnd.x - vector.x * radius, y: rawEnd.y - vector.y * radius }
      : rawEnd;
    if (Math.hypot(end.x - start.x, end.y - start.y) > epsilon) {
      pieces.push(computeSegmentBetween(start, end, sizing));
    }

    if (corners[endVertex] && !curve180Entry[endVertex] && !curve180Exit[endVertex]) {
      const nextHeading = headings[(segmentIndex + 1) % segmentCount];
      const turn = normDeg(nextHeading - heading);
      pieces.push(
        computeFilletCurve(
          end,
          heading,
          { ...sizing, radiusUnits: radius },
          turn === 90 ? 1 : -1
        )
      );
    }
  }

  return { ok: true, pieces };
}
